import React, { Component } from "react";
import styled from "styled-components";
import {
    DialogTitle,
    DialogActions,
    Button
} from "@material-ui/core";

import { AssetType } from "common/constants";
import { Field } from "js/react/components/UiComponents";
import TagsList from "js/react/components/TagsList";
import AddTagsDialog from "js/react/components/Dialogs/AddTagsDialog";
import { BeautifulDialog, DialogContent, ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { t } from "js/i18n";
import { ImageOption, ImageOptionList } from "js/Components/ImageOptionList";

const StyledDialogTitle = styled(DialogTitle)`
    && {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
`;

const TitleText = styled.div`
    color: #333;
    font-size: 23px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0;
`;

const SubText = styled.div`
    color: #666;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.25;
    letter-spacing: 0;
    margin-top: 5px;
`;

const StyledDialogContent = styled(DialogContent)`
    && {
        padding: 0 50px;
        overflow-y: hidden;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    && {
        padding-top: 18px;
    }
`;

const ASSET_TYPE_OPTIONS = [
    {
        label: "Image",
        value: AssetType.IMAGE,
        icon: "camera_alt",
    },
    {
        label: "Logo",
        value: AssetType.LOGO,
        icon: "sort_by_alpha",
    },
];

const AssetTypeOptions = styled(ImageOptionList)`
    && {
        width: max-content;
    }
`;

const AssetTypeOptionsWrapper = styled.div`
    margin-top: 8px;
`;

const TagsListWrapper = styled.div`
    margin-top: 8px;
`;

const AssetTypeOption = styled(ImageOption)`
    && {
        .withlabel label {
            color: #444;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.5px;
            line-height: 1.2;
        }

        .icon {
            min-height: 0;
        }

        .bai-icon {
            font-size: 19px;
        }
    }
`;

class BulkAssetUploadDialogComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            types: props.types ?? "image",
            tags: props.tags ?? []
        };
    }

    handleAccept = () => {
        const { closeDialog, onAccept } = this.props;
        const { types, tags } = this.state;
        closeDialog();
        onAccept({ types, tags });
    }

    handleRemoveTag = tag => {
        const { tags } = this.state;
        let index = tags.indexOf(tag);
        if (index > -1) {
            tags.splice(index, 1);
            this.setState({
                tags,
            });
        }
    }

    handleAddTag = () => {
        const { existingTags } = this.props;
        const { tags } = this.state;

        ShowDialog(AddTagsDialog, {
            activeTags: tags,
            existingTags,
            acceptCallback: newTags => {
                this.setState({
                    tags: newTags
                });
            },
        });
    }

    handleSelectType = value => {
        this.setState({
            types: value,
        });
        return true;
    }

    render() {
        const { files, closeDialog } = this.props;
        const { tags, types } = this.state;

        return (
            <BeautifulDialog closeDialog={closeDialog}>
                <StyledDialogTitle>
                    <TitleText>{t("Bulk upload assets")}</TitleText>
                    <SubText>
                        {files.length === 1
                            ? t("Share {{count}} asset with your team. Updates will sync everywhere it's used.", { count: files.length })
                            : t("Share {{count}} assets with your team. Updates will sync everywhere they're used.", { count: files.length })}
                    </SubText>
                </StyledDialogTitle>

                <StyledDialogContent>
                    <Field title={t("Assets Type")}>
                        <AssetTypeOptionsWrapper>
                            <AssetTypeOptions value={types} onChange={this.handleSelectType} size={34} gap={10}>
                                {ASSET_TYPE_OPTIONS.map(option => (
                                    <AssetTypeOption
                                        key={option.value}
                                        value={option.value}
                                        icon={option.icon}
                                        label={t(option.label)}
                                        labelGap={4}
                                        background="#f7f9fb"
                                    />
                                ))}
                            </AssetTypeOptions>
                        </AssetTypeOptionsWrapper>
                    </Field>
                    <Field title={t("Tags (optional)")}>
                        <TagsListWrapper>
                            <TagsList
                                tags={tags}
                                onRemove={this.handleRemoveTag}
                                onClick={this.handleAddTag}
                            />
                        </TagsListWrapper>
                    </Field>
                </StyledDialogContent>

                <StyledDialogActions>
                    <Button onClick={closeDialog}>{t("Cancel")}</Button>
                    <Button onClick={this.handleAccept} color="primary">{t("Upload Assets")}</Button>
                </StyledDialogActions>
            </BeautifulDialog>
        );
    }
}

const BulkAssetUploadDialog = BulkAssetUploadDialogComponent;

class BulkSetTagsDialogComponent extends Component {
    state = {
        tags: []
    }

    handleRemoveTag = tag => {
        const { tags } = this.state;
        let index = tags.indexOf(tag);
        if (index > -1) {
            tags.splice(index, 1);
            this.setState({
                tags,
            });
        }
    }

    handleAddTag = () => {
        const { existingTags } = this.props;
        const { tags } = this.state;

        ShowDialog(AddTagsDialog, {
            activeTags: tags,
            existingTags,
            acceptCallback: newTags => {
                this.setState({
                    tags: newTags
                });
            },
        });
    }

    handleAccept = () => {
        const { tags } = this.state;
        const { closeDialog, onAccept } = this.props;

        if (tags.length === 0) {
            closeDialog();
            return;
        }
        closeDialog();
        onAccept(tags);
    }

    render() {
        const { selection, closeDialog } = this.props;
        const { tags } = this.state;

        return (
            <BeautifulDialog closeDialog={closeDialog}>
                <StyledDialogTitle>
                    <TitleText> {t("Add tags to {{count}} selected assets", { count: selection.length })}</TitleText>
                    <SubText>
                        {t("Existing tags will not be removed.")}
                    </SubText>
                </StyledDialogTitle>

                <DialogContent>
                    <TagsList
                        tags={tags}
                        onRemove={this.handleRemoveTag}
                        onClick={this.handleAddTag}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={closeDialog}>{t("Cancel")}</Button>
                    <Button onClick={this.handleAccept} color="primary" disabled={tags.length === 0}>{t("Add Tags")}</Button>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}

const BulkSetTagsDialog = BulkSetTagsDialogComponent;

export { BulkAssetUploadDialog, BulkSetTagsDialog };
